import React from 'react';
import { createTheme, GlobalStyles, Theme } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';
// Pull in @mui/x-tree-view theme augmentation (MuiTreeItem, etc.)
import type {} from '@mui/x-tree-view/themeAugmentation';
// Pull in @mui/x-date-pickers theme augmentation (MuiPickersTextField, etc.)
import type {} from '@mui/x-date-pickers/themeAugmentation';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

export const theme = createTheme({
  typography: {
    fontFamily: ['Poppins', 'sans-serif'].join(',')
  },
  palette: {
    primary: {
      main: '#34AEA7'
    },
    secondary: {
      main: '#817A73'
    },
    error: {
      main: '#DB2E2D'
    }
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false
      }
    },
    MuiPopover: {
      defaultProps: {
        elevation: 1
      }
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: false
      }
    },
    MuiTreeItem: {
      styleOverrides: {
        content: {
          '&:hover': {
            backgroundColor: 'transparent'
          },
          // X Tree View v9 replaced the Mui-focused / Mui-selected state classes
          // with data-* attributes on the content element.
          '&[data-focused]': {
            backgroundColor: 'transparent'
          },
          '&[data-selected]': {
            backgroundColor: 'transparent',
            border: '1px solid',
            borderColor: '#34AEA7',
            '&:hover': {
              backgroundColor: 'transparent'
            },
            '&[data-focused]': {
              backgroundColor: 'transparent'
            }
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Poppins,sans-serif'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#000000',
          fontSize: '17px',
          fontWeight: 500,
          fontFamily: 'Poppins,sans-serif'
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #EBEDF2',
          // MUI v5+ drops the top padding after a DialogTitle, so a leading field's floated label is clipped.
          '.MuiDialogTitle-root + &': {
            paddingTop: '16px'
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '4px'
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: '#000000',
          fontFamily: 'Poppins,sans-serif',
          fontSize: '13px',
          fontWeight: 200
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '13px'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '13px'
        }
      }
    },
    // Date pickers render their own input components and do not inherit the
    // MuiTextField / MuiInputBase config above — mirror it explicitly.
    MuiPickersTextField: {
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiPickersInputBase: {
      styleOverrides: {
        input: {
          fontSize: '13px'
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0,0,0,0.7) !important'
        }
      }
    },
    MuiTableRow: {
      defaultProps: {
        hover: true
      },
      styleOverrides: {
        root: {
          backgroundColor: '#fff'
        },
        hover: {
          cursor: 'pointer'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '14px',
          fontFamily: 'Poppins,sans-serif'
        }
      }
    }
  }
});

// GridLegacy gave every container `width: 100%`; Grid v2 does not, so nested
// containers inside a flex parent shrink to their content. Restore it for
// containers without a `size` (sized ones get their width from `size`).
// `:where()` keeps specificity at zero so any makeStyles/sx width still wins.
const gridLegacyContainerWidth = {
  ':where(.MuiGrid-container:not([class*="MuiGrid-grid-"]))': {
    width: '100%'
  }
};

// Grid v2 spaces items with `gap`; GridLegacy pulled the container out by a negative top/left
// margin and padded each item by the spacing instead. Screens were tuned against the legacy model
// (many zero the container's margin, which exposes the item padding), so recreate it here rather
// than re-tuning every screen. MUI only adds a `MuiGrid-spacing-*` class when spacing > 0.
const SPACED = '.MuiGrid-container[class*="MuiGrid-spacing-"]';
const UNSPACED = '.MuiGrid-container:not([class*="MuiGrid-spacing-"])';

const gridLegacySpacing = {
  // A nested v2 container with no `spacing` inherits its parent's through these variables, which
  // GridLegacy never did.
  [`:where(${UNSPACED})`]: {
    '--Grid-rowSpacing': '0px',
    '--Grid-columnSpacing': '0px'
  },
  [`:where(${UNSPACED}) > *`]: {
    '--Grid-parent-rowSpacing': '0px',
    '--Grid-parent-columnSpacing': '0px'
  },
  // Zero specificity, as GridLegacy's margin/width lost to a screen's own `margin: 0` etc.
  [`:where(${SPACED})`]: {
    marginTop: 'calc(-1 * var(--Grid-rowSpacing))',
    marginLeft: 'calc(-1 * var(--Grid-columnSpacing))'
  },
  [`:where(${SPACED}:not([class*="MuiGrid-grid-"]))`]: {
    width: 'calc(100% + var(--Grid-columnSpacing))'
  },
  // These must beat Grid v2's own single-class rules.
  [`.MuiGrid-root${SPACED}`]: {
    gap: 0
  },
  [`.MuiGrid-root${SPACED} > .MuiGrid-root`]: {
    paddingTop: 'var(--Grid-parent-rowSpacing)',
    paddingLeft: 'var(--Grid-parent-columnSpacing)'
  },
  // v2 subtracts the gap from each item's width; with padding instead, an item is a plain fraction.
  // A sized item that is also a spaced container was widened by its own spacing, to cancel its
  // negative margin. These outrank v2's own size rules, so each breakpoint must also restate `auto`
  // (v2's only other width), or a smaller breakpoint's number would stick: { xs: 12, md: 'auto' }
  // would stay 100%. Breakpoints are in ascending order, so a larger one still wins, as in v2.
  ...Object.fromEntries(
    theme.breakpoints.keys.map((breakpoint) => [
      theme.breakpoints.up(breakpoint),
      Object.fromEntries(
        [`.MuiGrid-root`, `.MuiGrid-root${SPACED}`].flatMap((element) => {
          const extra = element === '.MuiGrid-root' ? '' : ' + var(--Grid-columnSpacing)';
          return [
            ...Array.from({ length: 12 }, (_, i) => i + 1).map((size) => [
              `${element}.MuiGrid-grid-${breakpoint}-${size}`,
              { width: `calc(100% * ${size} / var(--Grid-parent-columns)${extra})` }
            ]),
            [`${element}.MuiGrid-grid-${breakpoint}-auto`, { width: 'auto' }]
          ];
        })
      )
    ])
  )
};

export function MaterialThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>
        <GlobalStyles styles={gridLegacyContainerWidth} />
        <GlobalStyles styles={gridLegacySpacing} />
        <LegacyThemeProvider theme={theme}>{children}</LegacyThemeProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
}
